// The template editor (spec/dashboard.md section 5.2): the template card (name validated live, display name from the
// name, kind locked after creation, the base template with the singleton proposal of Q-D-15, flags, the display format
// with its token palette and a live preview, a singleton's default document) saved as one PATCH — or the POST that
// creates the template with its parameters — and the parameter table with the drawer of section 5.3.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ops } from '../../api/operations';
import type { Document, Json, Parameter, Template } from '../../api/types';
import { autoFormat, formatTokens, formatValue, labelText } from '../../content/format';
import { canBeBaseOf, displayNameFrom, KIND_LABEL, TEMPLATE_FLAGS, templateNameError, typeDefault, typeLabel, type TemplateKind } from '../../content/schema';
import { heirsOf } from '../../content/widgets/context';
import { routes } from '../../shell/routes';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Combobox } from '../../ui/Combobox';
import { Field } from '../../ui/Field';
import { EmptyState } from '../../ui/Banner';
import { ArrowDown, ArrowUp, FileText, Layers, MoreVertical, Plus, SlidersHorizontal } from '../../ui/icons';
import { Select, Switch, TextArea, TextInput } from '../../ui/inputs';
import { Modal, useUrlParam } from '../../ui/Modal';
import { Menu } from '../../ui/Popover';
import { useQuery } from '../../workspace/cache';
import { useWorkspaceVersion } from '../../workspace/react';
import { entityKey } from '../../workspace/Workspace';
import { copyName, Dot, familyDocumentCount, fieldErrors, FlagChips, parameterRequest, SchemaTabs, useSchemaEnv, useTemplateActions } from './common';
import { BaseLink, ParameterDrawer } from './ParameterDrawer';
import './schema.css';

/** The base a new singleton is offered (Q-D-15). */
export const CONDITIONAL_TEMPLATE = 'SmartObjects.ConditionalTemplate';
export const SINGLETON_HINT = 'a singleton usually inherits ConditionalTemplate (condition, priority); keep None for a plain singleton';

const CREATABLE_KINDS: TemplateKind[] = ['document', 'component', 'singleton', 'inline', 'profile', 'profile_root'];

interface Card {
  name: string;
  displayName: string;
  displayNameTouched: boolean;
  description: string;
  kind: TemplateKind;
  parent: string | null;
  flags: string[];
  customFormat: boolean;
  displayFormat: string;
}

function cardOf(t: Template | null | undefined): Card {
  if (!t) return { name: '', displayName: '', displayNameTouched: false, description: '', kind: 'document', parent: null, flags: [], customFormat: false, displayFormat: '' };
  const format = t.displayFormat ?? '';
  return { name: t.name, displayName: t.displayName ?? '', displayNameTouched: true, description: t.description ?? '', kind: t.kind, parent: t.parent ?? null, flags: [...t.flags], customFormat: format.trim().length > 0, displayFormat: format };
}

const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

export function TemplateEditorPage() {
  const { template: routeName } = useParams();
  // a fresh editor per template: the card state never leaks from one template into the next
  return <TemplateEditor key={routeName ?? '$new'} routeName={routeName ?? null} />;
}

function TemplateEditor({ routeName }: { routeName: string | null }) {
  const env = useSchemaEnv();
  const { client, path, catalog, workspace, toasts, confirm, navigate, canWrite, afterWrite, fail, org, gameSlug, gameId, branch } = env;
  useWorkspaceVersion(workspace);
  const creating = routeName === null;
  const t = creating ? null : (catalog.templates.get(routeName) ?? null);
  const actions = useTemplateActions();
  const paramUrl = useUrlParam('param');

  const [card, setCard] = useState<Card>(() => cardOf(t));
  const [proposed, setProposed] = useState(false);
  const [localParams, setLocalParams] = useState<Parameter[]>([]);
  const [orderDraft, setOrderDraft] = useState<string[] | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const formatRef = useRef<HTMLTextAreaElement>(null);

  // the server's values when they arrive or move (after a save, a remote change)
  const serverKey = t ? `${t.name}@${t.version}` : null;
  useEffect(() => {
    if (!creating && t) {
      setCard(cardOf(t));
      setOrderDraft(null);
      setServerErrors({});
    }
  }, [serverKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<Card>) => setCard((c) => ({ ...c, ...patch }));
  const server = cardOf(t);
  const dirty = creating ? card.name.length > 0 : JSON.stringify({ ...card, displayNameTouched: true }) !== JSON.stringify(server);

  // live name validation: the pattern and the reserved prefixes; a template already in a system namespace keeps it
  const sameNamespace = !!t && t.name.split('.')[0] === card.name.split('.')[0];
  const nameError = templateNameError(card.name, { allowReserved: sameNamespace }) ?? (card.name !== t?.name && catalog.templates.has(card.name) ? `A template named ${card.name} already exists.` : null);

  const heirNames = useMemo(() => new Set(t ? heirsOf(catalog, t.name).map((h) => h.name) : []), [catalog, t]);
  const baseOptions = useMemo(
    () =>
      catalog.templateList
        .filter((b) => canBeBaseOf(b, card.kind) && b.name !== t?.name && !heirNames.has(b.name))
        .map((b) => ({ value: b.name, label: b.displayName ?? b.name, hint: `${b.name} · ${b.kind}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [catalog.templateList, card.kind, t, heirNames],
  );
  const conditional = catalog.templates.get(CONDITIONAL_TEMPLATE);

  const chooseKind = (kind: TemplateKind) => {
    // a new singleton is offered ConditionalTemplate as its base, visibly, with None one click away (Q-D-15)
    if (kind === 'singleton' && card.parent === null && conditional && canBeBaseOf(conditional, kind)) {
      set({ kind, parent: CONDITIONAL_TEMPLATE });
      setProposed(true);
      return;
    }
    if (proposed && card.parent === CONDITIONAL_TEMPLATE) {
      set({ kind, parent: null });
      setProposed(false);
      return;
    }
    set({ kind, parent: card.parent && catalog.templates.get(card.parent) && !canBeBaseOf(catalog.templates.get(card.parent)!, kind) ? null : card.parent });
  };

  // the parameters: effective ones in the template's order (or the reordered draft); the create state edits a local list
  const baseParams = useMemo(() => (card.parent ? (catalog.templates.get(card.parent)?.effectiveParameters ?? []) : []), [catalog.templates, card.parent]);
  const effective: Parameter[] = useMemo(() => {
    if (creating) return [...baseParams, ...localParams];
    const list = t?.effectiveParameters ?? [];
    if (!orderDraft) return list;
    return [...list].sort((a, b) => orderDraft.indexOf(a.name) - orderDraft.indexOf(b.name));
  }, [creating, baseParams, localParams, t, orderDraft]);
  const ownName = creating ? card.name || 'New template' : (t?.name ?? '');
  const isInherited = (p: Parameter) => (creating ? !localParams.includes(p) : !!p.declaredBy && p.declaredBy !== t?.name);
  const familyDocs = t ? familyDocumentCount(catalog, t) : 0;

  // the display format's preview: the first document of the template, or the defaults
  const firstQ = useQuery(t ? `branch:${gameId}:${branch}:templates:${t.name}:first-document` : null, () => client.data(ops.listDocuments, { path, query: { template: t!.name, limit: 1 } }), { head: workspace.head });
  const previewTemplate: Template = useMemo(
    () => ({
      ...(t ?? { id: '', unnyId: null, description: null, legacyFlags: 0, order: [], parameters: localParams, selected: null, documentCount: 0, version: 0 }),
      name: card.name || 'New',
      displayName: card.displayName || displayNameFrom(card.name) || 'New template',
      kind: card.kind,
      parent: card.parent,
      flags: card.flags,
      displayFormat: card.customFormat ? card.displayFormat : null,
      effectiveParameters: effective,
    }),
    [t, localParams, card, effective],
  );
  const previewDoc = useMemo(() => {
    const first = (firstQ.data?.items ?? [])[0] as Document | undefined;
    if (first) return first;
    const data: Record<string, Json> = {};
    for (const p of effective) data[p.name] = typeDefault(p);
    return { id: '', unnyId: null, template: previewTemplate.name, data };
  }, [firstQ.data, effective, previewTemplate.name]);
  const preview = labelText(previewDoc, catalog.format, previewTemplate);

  const insertToken = (token: string) => {
    const el = formatRef.current;
    const text = card.displayFormat;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + token + text.slice(end);
    set({ displayFormat: next });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const save = async () => {
    if (nameError) return;
    setServerErrors({});
    const displayFormat = card.customFormat ? card.displayFormat : null;
    if (creating) {
      setBusy(true);
      try {
        const result = await client.call(ops.createTemplate, {
          path,
          body: {
            name: card.name,
            displayName: card.displayName || null,
            description: card.description || null,
            kind: card.kind,
            parent: card.parent,
            flags: card.flags,
            displayFormat,
            order: null,
            parameters: localParams.map((p) => parameterRequest(p)) as never,
          },
        });
        if (result.warning) toasts.warning(`Warning: ${result.warning}`);
        toasts.success(`Template ${result.data.name} created.`);
        afterWrite(result.commit);
        navigate(routes.template(org, gameSlug, branch, result.data.name), { replace: true });
      } catch (e) {
        setServerErrors(fieldErrors(e));
        fail(e, `Creating ${card.name} failed`);
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!t) return;
    const body: Record<string, unknown> = {};
    if (card.name !== server.name) body.name = card.name;
    if (card.displayName !== server.displayName) body.displayName = card.displayName;
    if (card.description !== server.description) body.description = card.description;
    if (card.parent !== server.parent) body.parent = card.parent ?? '';
    if (!sameList(card.flags, server.flags)) body.flags = card.flags;
    const oldFormat = server.customFormat ? server.displayFormat : '';
    const newFormat = displayFormat ?? '';
    if (oldFormat !== newFormat) body.displayFormat = newFormat;
    if (Object.keys(body).length === 0) return;
    if (body.parent !== undefined) {
      // the inherited parameters gained and lost, before the PATCH
      const names = (base: string | null) => new Set(base ? (catalog.templates.get(base)?.effectiveParameters ?? []).map((p) => p.name) : []);
      const before = names(server.parent);
      const after = names(card.parent);
      const gained = [...after].filter((n) => !before.has(n));
      const lost = [...before].filter((n) => !after.has(n));
      const ok = await confirm({
        title: `Change the base of ${t.displayName ?? t.name}?`,
        body: `From ${server.parent ?? 'None'} to ${card.parent ?? 'None'}.`,
        impact: (
          <div className="stack">
            <div>Inherited parameters gained: {gained.length > 0 ? gained.join(', ') : 'none'}</div>
            <div>Inherited parameters lost: {lost.length > 0 ? lost.join(', ') : 'none'}</div>
          </div>
        ),
        confirmLabel: 'Change base',
        tone: lost.length > 0 ? 'danger' : 'primary',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const result = await client.call(ops.updateTemplate, { path: { ...path, template: t.name }, ifMatch: Number(t.version), body: body });
      if (result.warning) toasts.warning(`Warning: ${result.warning}`);
      toasts.success(`Template ${result.data.name} saved.`);
      afterWrite(result.commit);
      if (result.data.name !== t.name) navigate(routes.template(org, gameSlug, branch, result.data.name), { replace: true });
    } catch (e) {
      setServerErrors(fieldErrors(e));
      fail(e, `Saving ${t.name} failed`);
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setCard(cardOf(t));
    setProposed(false);
    setServerErrors({});
    if (creating) setLocalParams([]);
  };

  // ---- the parameter table's actions
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= effective.length) return;
    if (creating) {
      const own = [...localParams];
      const a = own.indexOf(effective[index]!);
      const b = own.indexOf(effective[target]!);
      if (a < 0 || b < 0) return;
      [own[a], own[b]] = [own[b]!, own[a]!];
      setLocalParams(own);
      return;
    }
    const names = effective.map((p) => p.name);
    [names[index], names[target]] = [names[target]!, names[index]!];
    setOrderDraft(names);
  };
  const saveOrder = async () => {
    if (!t || !orderDraft) return;
    setBusy(true);
    try {
      const result = await client.call(ops.updateTemplate, { path: { ...path, template: t.name }, ifMatch: Number(t.version), body: { order: orderDraft } });
      toasts.success('Order saved.');
      afterWrite(result.commit);
    } catch (e) {
      fail(e, 'Saving the order failed');
    } finally {
      setBusy(false);
    }
  };
  const cloneParam = async (p: Parameter) => {
    const copy: Parameter = { ...p, id: '', name: `${p.name}Copy`, displayName: p.displayName ? `${p.displayName} Copy` : null, declaredBy: ownName };
    if (creating) {
      setLocalParams((list) => [...list, copy]);
      return;
    }
    if (!t) return;
    try {
      const result = await client.call(ops.addParameter, { path: { ...path, template: t.name }, ifMatch: Number(t.version), body: parameterRequest(copy) });
      toasts.success(`Parameter ${copy.name} added.`);
      afterWrite(result.commit);
    } catch (e) {
      fail(e, `Cloning ${p.name} failed`);
    }
  };
  const deleteParam = async (p: Parameter) => {
    const ok = await confirm({
      title: `Delete parameter ${p.displayName ?? p.name}?`,
      body: creating ? `${p.name} is removed from the new template.` : `The value of ${p.name} is removed from ${familyDocs} document${familyDocs === 1 ? '' : 's'} of the ${t?.name} family. The deletion is a commit of its own.`,
      confirmLabel: 'Delete parameter',
      tone: 'danger',
    });
    if (!ok) return;
    if (creating) {
      setLocalParams((list) => list.filter((x) => x !== p));
      return;
    }
    if (!t) return;
    try {
      const result = await client.call(ops.deleteParameter, { path: { ...path, template: t.name, param: p.name }, ifMatch: Number(t.version) });
      toasts.success(`Parameter ${p.name} deleted.`);
      afterWrite(result.commit);
    } catch (e) {
      fail(e, `Deleting ${p.name} failed`);
    }
  };

  // ---- the drawer
  const drawerValue = paramUrl.value;
  const drawerParam = drawerValue && drawerValue !== 'new' ? (effective.find((p) => p.name === drawerValue) ?? null) : null;
  const takenNames = useMemo(() => [...effective.map((p) => p.name), ...(t ? heirsOf(catalog, t.name).flatMap((h) => h.parameters.map((p) => p.name)) : [])], [effective, catalog, t]);

  if (!creating && !t) {
    return (
      <section className="schema-page">
        <div className="page-head">
          <h1>Data structure</h1>
        </div>
        <SchemaTabs />
        <div className="pane">
          {catalog.loading ? (
            <div className="skeleton-rows" role="status" aria-label="Loading">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="skeleton-row">
                  <span className="skeleton" />
                  <span className="skeleton" />
                </div>
              ))}
            </div>
          ) : (
            <div className="pane-body" role="alert">
              <EmptyState
                icon={<Layers />}
                title={`No template named ${routeName} on this branch`}
                body="It may have been renamed or deleted, or it lives on another branch."
                action={
                  <Link className="btn btn-s" to={routes.templates(org, gameSlug, branch)}>
                    All templates
                  </Link>
                }
              />
            </div>
          )}
        </div>
        {actions.jobDialog}
      </section>
    );
  }

  const selectedDraft = t ? (workspace.getDraft(entityKey('template-selected', t.name))?.current as { selected?: string | null } | undefined) : undefined;
  const selected = selectedDraft?.selected ?? t?.selected ?? null;
  const selectedDoc = selected ? catalog.resolve(selected) : undefined;

  return (
    <section className="template-editor schema-page">
      <div className="page-head">
        <h1>{creating ? 'New template' : (t!.displayName ?? t!.name)}</h1>
        {t && <code>{t.name}</code>}
        {t && <Badge tone={t.kind === 'document' ? 'accent' : 'neutral'}>{KIND_LABEL[t.kind]}</Badge>}
        <span className="spacer" />
        {t && (
          <>
            <Link className="btn" to={routes.documents(org, gameSlug, branch, t.name)}>
              <FileText aria-hidden="true" />
              Open documents
            </Link>
            <IconButton label="Template actions" aria-haspopup="menu" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <MoreVertical aria-hidden="true" />
            </IconButton>
            <Menu
              anchor={menuAnchor}
              open={menuAnchor !== null}
              onClose={() => setMenuAnchor(null)}
              label="Template actions"
              items={[
                { key: 'docs', label: 'Open documents', onSelect: () => navigate(routes.documents(org, gameSlug, branch, t.name)) },
                { key: 'clone', label: `Clone template (${copyName(t.name)})`, onSelect: () => void actions.clone(t), disabled: !canWrite },
                { key: 's', label: '', separator: true },
                { key: 'delete', label: 'Delete template', danger: true, disabled: !canWrite, onSelect: () => void actions.remove(t).then((ok) => ok && navigate(routes.templates(org, gameSlug, branch))) },
              ]}
            />
          </>
        )}
      </div>
      <SchemaTabs />
      <div className="schema-editor">
        <form
          className="pane schema-card"
          aria-label="Template"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div className="pane-head">
            <h2>Template</h2>
            <span className="muted small">Identity, kind, base and how its documents are labelled</span>
          </div>
          <div className="pane-body form-grid">
            <Field label="Name" required error={nameError ?? serverErrors.name} help="Dotted namespaces, every segment starts with a capital letter (Hue.Items.Sword).">
              <TextInput className="mono" value={card.name} disabled={!canWrite} autoComplete="off" spellCheck={false} onChange={(e) => set({ name: e.target.value, ...(card.displayNameTouched ? {} : { displayName: displayNameFrom(e.target.value) }) })} />
            </Field>
            <Field label="Display name" error={serverErrors.displayName}>
              <TextInput value={card.displayName} disabled={!canWrite} onChange={(e) => set({ displayName: e.target.value, displayNameTouched: true })} />
            </Field>
            <Field label="Kind" help={creating ? undefined : 'The kind is fixed once the template exists.'} error={serverErrors.kind}>
              <Select value={card.kind} disabled={!creating || !canWrite} onChange={(e) => chooseKind(e.target.value as TemplateKind)}>
                {CREATABLE_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Base template" error={serverErrors.parent} help={card.kind === 'singleton' && creating && conditional ? SINGLETON_HINT : undefined}>
              <Combobox
                value={card.parent}
                options={baseOptions}
                emptyLabel="None"
                disabled={!canWrite || card.kind === 'inline' || card.kind === 'profile' || card.kind === 'profile_root'}
                onChange={(v) => {
                  set({ parent: v });
                  setProposed(false);
                }}
              />
            </Field>
            {creating && card.kind === 'singleton' && conditional && (
              <div className="proposal span-2" role="note">
                <span>{card.parent === CONDITIONAL_TEMPLATE ? `Proposed base: ${CONDITIONAL_TEMPLATE}.` : `Proposed base: ${CONDITIONAL_TEMPLATE} (not chosen).`}</span>
                {card.parent === CONDITIONAL_TEMPLATE ? (
                  <Button size="s" onClick={() => { set({ parent: null }); setProposed(false); }}>
                    Keep None
                  </Button>
                ) : (
                  <Button size="s" onClick={() => set({ parent: CONDITIONAL_TEMPLATE })}>
                    Use {CONDITIONAL_TEMPLATE.split('.').pop()}
                  </Button>
                )}
              </div>
            )}
            <Field className="span-2" label="Description" error={serverErrors.description}>
              <TextArea rows={2} value={card.description} disabled={!canWrite} onChange={(e) => set({ description: e.target.value })} />
            </Field>
            <Field className="span-2" label="Flags" error={serverErrors.flags}>
              <FlagChips label="Template flags" catalog={TEMPLATE_FLAGS} value={card.flags} onChange={(flags) => set({ flags })} disabled={!canWrite} />
            </Field>
            <div className="field span-2 format-block">
              <Switch label="Custom display format" checked={card.customFormat} disabled={!canWrite} onChange={(v) => set({ customFormat: v, displayFormat: v && !card.displayFormat ? autoFormat(previewTemplate) : card.displayFormat })} />
              {card.customFormat ? (
                <>
                  <Field label="Display format" error={serverErrors.displayFormat} help="Literal text and tokens; {icon(circle, text, #color)} draws a badge.">
                    <TextArea className="mono" ref={formatRef} rows={2} spellCheck={false} value={card.displayFormat} disabled={!canWrite} onChange={(e) => set({ displayFormat: e.target.value })} />
                  </Field>
                  <div className="token-palette" role="group" aria-label="Format tokens">
                    {formatTokens(previewTemplate).map((tok) => (
                      <button key={tok.token} type="button" className="chip chip-outline token-chip" title={tok.token} disabled={!canWrite} onMouseDown={(e) => e.preventDefault()} onClick={() => insertToken(tok.token)}>
                        {tok.label}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="field-help">
                  Auto format: <code>{autoFormat(previewTemplate)}</code>
                </div>
              )}
              <div className="format-preview" aria-label="Display format preview">
                <span className="muted">Preview{(firstQ.data?.items ?? []).length > 0 ? ' (first document)' : ' (defaults)'}: </span>
                <output>{preview}</output>
              </div>
            </div>
            {t?.kind === 'singleton' && (
              <Field className="span-2" label="Default document" help="The document the engine uses when no higher-priority one passes its condition; chosen in the Default column of the documents grid (staged).">
                <span className="row">
                  <output>{selected ? (selectedDoc ? catalog.label(selectedDoc) : selected.slice(0, 8)) : 'None'}</output>
                  {selectedDraft && <Badge tone="warning">staged</Badge>}
                  <Link to={routes.documents(org, gameSlug, branch, t.name)}>Choose…</Link>
                </span>
              </Field>
            )}
          </div>
          <div className="pane-foot">
            {dirty && <span className="muted small">{creating ? 'The template and its parameters are created in one commit.' : 'The template has unsaved changes.'}</span>}
            <span className="spacer" />
            <Button onClick={cancel} disabled={!dirty || busy}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" busy={busy} disabled={!dirty || !!nameError || !canWrite}>
              {creating ? 'Create template' : 'Save'}
            </Button>
          </div>
        </form>

        <section className="pane schema-params" aria-label="Parameters">
          <div className="pane-head">
            <h2>Parameters</h2>
            <span className="chip">{effective.length}</span>
            <span className="spacer" />
            {orderDraft && (
              <>
                <Badge tone="warning">order changed</Badge>
                <Button size="s" variant="ghost" onClick={() => setOrderDraft(null)}>
                  Reset order
                </Button>
                <Button size="s" variant="primary" busy={busy} onClick={() => void saveOrder()} disabled={!canWrite}>
                  Save order
                </Button>
              </>
            )}
            <Button size="s" disabled={!canWrite} onClick={() => paramUrl.open('new')} icon={<Plus aria-hidden="true" />}>
              Add parameter
            </Button>
          </div>
          {effective.length === 0 ? (
            <div className="pane-body">
              <EmptyState icon={<SlidersHorizontal />} title="No parameters yet" body="A parameter is one field of every document of the template; Add parameter creates the first." />
            </div>
          ) : (
            <div className="pane-body flush schema-table-wrap">
              <table className="grid schema-table">
                <thead>
                  <tr>
                    <th className="col-order">
                      <span className="visually-hidden">Order</span>
                    </th>
                    <th>Parameter</th>
                    <th>Type</th>
                    <th>Default</th>
                    <th title="Required / Unique / In name">Req · Uniq · Name</th>
                    <th>Flags</th>
                    <th>Description</th>
                    <th className="col-menu" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {effective.map((p, i) => {
                    const inherited = isInherited(p);
                    return (
                      <tr key={p.name} data-testid={`param-${p.name}`} className={`grid-row ${inherited ? 'inherited' : ''}`}>
                        <td className="col-order">
                          <span className="order-buttons">
                            <IconButton label={`Move ${p.name} up`} disabled={i === 0 || !canWrite || (creating && inherited)} onClick={() => move(i, -1)}>
                              <ArrowUp aria-hidden="true" />
                            </IconButton>
                            <IconButton label={`Move ${p.name} down`} disabled={i === effective.length - 1 || !canWrite || (creating && inherited)} onClick={() => move(i, 1)}>
                              <ArrowDown aria-hidden="true" />
                            </IconButton>
                          </span>
                        </td>
                        <td>
                          <div className="param-cell">
                            {inherited ? (
                              <span className="param-name">
                                <code>{p.name}</code> <span className="inherited-from">[<BaseLink base={p.declaredBy ?? card.parent ?? ''} />]</span>
                              </span>
                            ) : (
                              <button type="button" className="link param-name mono" onClick={() => paramUrl.open(p.name)}>
                                {p.name}
                              </button>
                            )}
                            {p.displayName && p.displayName !== p.name && <span className="muted small">{p.displayName}</span>}
                          </div>
                        </td>
                        <td>{typeLabel(p, catalog.enums, catalog.templates)}</td>
                        <td>{p.default === null || p.default === undefined ? <span className="muted">—</span> : formatValue(p, p.default as Json, catalog.format)}</td>
                        <td>
                          <span className="dots">
                            <Dot on={p.isRequired} label="Required" /> <Dot on={p.isUnique} label="Unique" /> <Dot on={p.usedInName} label="In name" />
                          </span>
                        </td>
                        <td className="small">{p.flags.join(', ')}</td>
                        <td className="small muted">{p.description ?? ''}</td>
                        <td className="col-menu">
                          <ParamMenu p={p} inherited={inherited} canWrite={canWrite} onEdit={() => paramUrl.open(p.name)} onBase={() => navigate(routes.template(org, gameSlug, branch, p.declaredBy ?? card.parent ?? ''))} onClone={() => void cloneParam(p)} onDelete={() => void deleteParam(p)} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {drawerValue && drawerParam && isInherited(drawerParam) ? (
        <Modal drawer open title={`Parameter ${drawerParam.name}`} onClose={paramUrl.close}>
          <p>
            {drawerParam.name} is inherited from <BaseLink base={drawerParam.declaredBy ?? card.parent ?? ''} />; it is edited on the base template.
          </p>
        </Modal>
      ) : drawerValue && (drawerValue === 'new' || drawerParam) ? (
        <ParameterDrawer
          key={drawerValue}
          template={t}
          templateName={ownName}
          parameter={drawerParam}
          takenNames={takenNames}
          familyDocuments={familyDocs}
          local={
            creating
              ? {
                  onSave: (p, previous) => setLocalParams((list) => (previous ? list.map((x) => (x.name === previous ? p : x)) : [...list, p])),
                  onDelete: (name) => setLocalParams((list) => list.filter((x) => x.name !== name)),
                }
              : undefined
          }
          onClose={paramUrl.close}
        />
      ) : drawerValue ? (
        <Modal drawer open title="Parameter" onClose={paramUrl.close}>
          <p className="error" role="alert">
            No parameter named {drawerValue} on {ownName}.
          </p>
        </Modal>
      ) : null}
      {actions.jobDialog}
    </section>
  );
}

function ParamMenu({ p, inherited, canWrite, onEdit, onBase, onClone, onDelete }: { p: Parameter; inherited: boolean; canWrite: boolean; onEdit: () => void; onBase: () => void; onClone: () => void; onDelete: () => void }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <IconButton label={`Actions for ${p.name}`} aria-haspopup="menu" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertical aria-hidden="true" />
      </IconButton>
      <Menu
        anchor={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        label={`Parameter ${p.name}`}
        items={
          inherited
            ? [{ key: 'base', label: `Edit on ${p.declaredBy ?? 'the base'}`, onSelect: onBase }]
            : [
                { key: 'edit', label: 'Edit', onSelect: onEdit },
                { key: 'clone', label: 'Clone', onSelect: onClone, disabled: !canWrite },
                { key: 's', label: '', separator: true },
                { key: 'delete', label: 'Delete', onSelect: onDelete, danger: true, disabled: !canWrite },
              ]
        }
      />
    </>
  );
}

